import {
  Combobox,
  useCombobox,
  ComboboxTarget,
  ComboboxDropdown,
  ComboboxOptions,
  ComboboxOption,
  Input,
  ScrollArea,
  Box,
  Group,
  Tooltip,
  CloseButton,
  InputBase,
  Text,
} from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { IconCalendar, IconCheck, IconSelector } from "@tabler/icons-react"
import { useRef } from "react"

import { DatePreset } from "@costory/types/prisma-client"

import { ICON_SIZE, ICON_STROKE } from "@costory/shared/const"
import { getDatePresetOptions } from "@costory/shared/utils/filters"

import { toReadableDate } from "@costory/front/utils/filters"

type DatePresetSelectProps = {
  value: string | null | undefined
  onChange: (val: string | null | undefined) => void
  from: string | null
  to: string | null
  onCustomDateChange?: (range: [string | null, string | null]) => void
  customDateRange?: [string | null, string | null]
  pickerRef?: React.RefObject<HTMLButtonElement | null>
  showLabel?: boolean
  allowNull?: boolean
}

export function DatePresetSelect({
  value,
  onChange,
  from,
  to,
  onCustomDateChange,
  customDateRange,
  pickerRef,
  showLabel = true,
  allowNull = false,
}: DatePresetSelectProps) {
  const combobox = useCombobox()

  const options = getDatePresetOptions()
  const customOption = { label: "Custom dates", value: null }
  const customReadableDate =
    from && to
      ? `${toReadableDate(from)} - ${toReadableDate(to)}`
      : "Select date range"

  const hiddenOptions: DatePreset[] = [
    DatePreset.TRAILING_14_WEEKS,
    DatePreset.LAST_4_YEARS,
    DatePreset.TRAILING_3_DAYS,
  ]
  const filteredOptions = options.filter(
    (opt) => !hiddenOptions.includes(opt.value),
  )
  const allOptions = [customOption, ...filteredOptions]
  const selectedOption = [customOption, ...options].find(
    (opt) => opt.value === value,
  )

  const internalRef = useRef<HTMLButtonElement>(null)
  const datePickerButtonRef = pickerRef ?? internalRef

  const getDisplayLabel = () => {
    if (!selectedOption || value === null) {
      // If no date range picked yet, prompt the user
      if (!from || !to) return "Select date range"
      return customReadableDate
    }
    return selectedOption.label
  }
  const tooltipLabel = (() => {
    if (!selectedOption || value === null) {
      if (!from || !to) return "Select date range"
      return customReadableDate
    }
    return customReadableDate
  })()
  return (
    <Box data-intercom-target="date-selector">
      <Combobox
        store={combobox}
        onOptionSubmit={(val) => {
          if (val === null || val === "custom") {
            onChange(null)
            // Optionally open the date picker
            datePickerButtonRef.current?.click?.()
          } else {
            onChange(val)
          }
          combobox.closeDropdown()
        }}
      >
        <ComboboxTarget>
          <Tooltip label={tooltipLabel}>
            <Input.Wrapper
              label={showLabel ? "Selected Date Preset" : undefined}
            >
              <InputBase
                component="button"
                value={getDisplayLabel()}
                onClick={() => combobox.toggleDropdown()}
                radius="md"
                miw={235}
                styles={{
                  input: {
                    cursor: "pointer",
                  },
                }}
                rightSection={
                  value && allowNull ? (
                    <CloseButton
                      size="sm"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        onChange(undefined)
                        onCustomDateChange?.([null, null])
                      }}
                      aria-label="Clear value"
                    />
                  ) : (
                    <IconSelector
                      style={{ cursor: "pointer" }}
                      size={ICON_SIZE}
                      stroke={ICON_STROKE}
                      onClick={() => combobox.toggleDropdown()}
                    />
                  )
                }
                leftSection={
                  <IconCalendar size={ICON_SIZE} stroke={ICON_STROKE} />
                }
              >
                {(from &&
                  allOptions.find((item) => item.value === value)?.label) || (
                  <Input.Placeholder>Update Date Range</Input.Placeholder>
                )}
              </InputBase>
            </Input.Wrapper>
          </Tooltip>
        </ComboboxTarget>

        <ComboboxDropdown>
          <ComboboxOptions>
            <ScrollArea h={250} scrollbarSize={4}>
              {allOptions.map((item) => (
                <ComboboxOption
                  key={item.value ?? "custom"}
                  value={item.value ?? "custom"}
                >
                  <Group gap="xs">
                    {(item.value === value ||
                      (item.value === null && value === null)) && (
                      <IconCheck size={ICON_SIZE} stroke={ICON_STROKE} />
                    )}
                    <Text fz="sm">{item.label}</Text>
                  </Group>
                </ComboboxOption>
              ))}
            </ScrollArea>
          </ComboboxOptions>
        </ComboboxDropdown>
      </Combobox>
      <DatePickerInput
        ref={datePickerButtonRef}
        type="range"
        value={customDateRange}
        onChange={onCustomDateChange}
        valueFormat="YYYY MMM DD"
        styles={{
          input: {
            position: "absolute",
            width: 0,
            height: 0,
            padding: 0,
            border: 0,
            overflow: "hidden",
          },
        }}
        popoverProps={{
          offset: -30,
        }}
      />
    </Box>
  )
}
